import alert from '@/plugins/alert'
import { BusStudentCheckin, Checkin, BusCheckin, StudentTracking, Class } from '@/plugins/api'
import { groupBy, keyBy } from 'lodash'
import moment from 'moment'

export default {
  namespaced: true,
  state: {
    attendances: {},
    pageText: '',
    itemsPerPage: 10,
    totalItems: 0,
    studentAttendances: [],
    date: moment().toISOString(),
    studentCheckins: [],
    loading: false,
    studentDetailAttendances: [],
    attendanceStudentSearchParams: {}
  },
  actions: {
    async fetchStudentTrackings({ commit }, params) {
      let countParams = { ...params, _limit: null, _start: null }
      const [totalItems, studentAttendances] = await Promise.all([
        StudentTracking.count(countParams),
        StudentTracking.fetch(params)
      ])
      const promises = await studentAttendances.map(async item => {
        let currentClass
        try {
          currentClass = await Class.fetchOne(item.student.currentClass)
        } catch (error) {
          console.error(error)
        }
        return {
          ...item,
          student: {
            ...item.student,
            currentClass: currentClass
          }
        }
      })
      let studenTracking = await Promise.all(promises)
      commit('changeState', { studentAttendances: studenTracking, totalItems })
      return studentAttendances
    },
    setPageText({ state, commit }, { page, itemsPerPage }) {
      const pageStart = (page - 1) * itemsPerPage + 1
      let pageStop = page * itemsPerPage
      pageStop = pageStop > state.totalItems || pageStop === -1 ? state.totalItems : pageStop
      let pageText = `${pageStart}-${pageStop} trên ${state.totalItems}`
      commit('changeState', { pageText })
    },
    async getStudentTrackingByPagination({ state, commit, dispatch }, { page, itemsPerPage }) {
      state.loading = true
      if (!page) page = 1
      if (!itemsPerPage) itemsPerPage = state.itemsPerPage
      let today = moment()
        .startOf('day')
        .toISOString()
      let tomorrow = moment()
        .startOf('day')
        .add(1, 'days')
        .toISOString()

      let params = {
        ...state.attendanceStudentSearchParams,
        _start: (page - 1) * itemsPerPage,
        _limit: itemsPerPage,
        createdAt_gt: today,
        createdAt_lt: tomorrow
      }
      await dispatch('fetchStudentTrackings', params)
      await dispatch('setPageText', { page, itemsPerPage })
      state.loading = false
    },
    async findStudentTracking({ state, dispatch, commit }, params) {
      state.loading = true
      const fetchParams = { ...params }
      if (!fetchParams._start) fetchParams._start = 0
      if (!fetchParams._limit) fetchParams._limit = state.itemsPerPage
      commit('changeState', { attendanceStudentSearchParams: fetchParams })
      await dispatch('fetchStudentTrackings', fetchParams)
      await dispatch('setDate', params.createdAt_gt)
      await dispatch('setPageText', { page: fetchParams._start + 1, itemsPerPage: fetchParams._limit })
      state.loading = false
    },
    async findOneStudentTracking(_, id) {
      try {
        let studentTracking = await BusStudentCheckin.fetchOne(id)
        let currentClass = await Class.fetchOne(studentTracking.student.currentClass)
        studentTracking.currentClass = currentClass
        return studentTracking
      } catch (e) {
        alert.error(e)
      }
    },

    async getAttendanceStudents({ commit, state }, query) {
      state.loading = true
      commit('changeState', { attendanceStudentSearchParams: query })
      let studentCheckins = await BusStudentCheckin.getBusAttendanceStudents(query)

      // mapping data
      const studentBusObj = {}
      let studentCheckinWithBus = groupBy(studentCheckins, 'student.id')
      for (const studentId of Object.keys(studentCheckinWithBus)) {
        const busTimelines = studentCheckinWithBus[studentId]
        let bus = groupBy(busTimelines, 'bus.id')
        for (const busId of Object.keys(bus)) {
          studentBusObj[`${studentId}-${busId}`] = {
            busId,
            studentId,
            busRowspan: bus[busId].length
          }
        }
      }

      studentCheckins = studentCheckins.map(sc => {
        sc.studentRowspan = studentCheckinWithBus[sc.student.id].length
        const sbObj = Object.values(studentBusObj).find(
          item => item.studentId === sc.student.id && item.busId === sc.bus.id
        )
        sc.studentBus = sc.student.id + '-' + sc.bus.id
        sc.busRowspan = sbObj.busRowspan
        return sc
      })
      commit('changeState', { studentCheckins })
      state.loading = false
    },
    async checkinAttendance({ commit }, data) {
      commit('setAttendance', await BusStudentCheckin.checkin(data))
    },
    async createAttendance({ commit }, data) {
      try {
        const att = await BusStudentCheckin.create(data)
        commit('setAttendance', att)
        return att
      } catch (e) {
        alert.error(e)
      }
    },
    async updateBusCheckinStatus({ commit }, params) {
      try {
        await BusCheckin.updateBusCheckinStatus(params)
      } catch (e) {
        console.error(e)
        alert.error(e)
      }
    },
    async createCheckin({ commit }, data) {
      try {
        await Checkin.create(data)
      } catch (e) {
        alert.error(e)
      }
    },
    async updateAttendance({ commit }, { id, ...attendance }) {
      const model = await BusStudentCheckin.update(id, attendance)
      commit('updateAttendance', model)
      return model
    },
    async updateCheckin({ commit }, { id, ...checkinData }) {
      await Checkin.update(id, checkinData)
    },
    setDate({ commit }, date) {
      commit('changeState', { date })
    },
    async fetchAttendancesInStudentDetail({ commit }, query) {
      var studentDetailAttendances = await BusStudentCheckin.fetch(query)
      studentDetailAttendances = studentDetailAttendances.map(a => {
        const at = { ...a, checkinObjects: a.checkins }
        return at
      })
      commit('changeState', { studentDetailAttendances })
    },
    async updateStatusAndTimeBusStudentTracking({ commit, state, dispatch }, { id, params }) {
      try {
        const newUpdate = await BusStudentCheckin.updateStatusAndTimeBusStudentTracking(id, params)
        dispatch('getStudentTrackingByPagination', { page: 1, itemsPerPage: state.itemsPerPage })
        alert.updateSuccess('Trạng thái học sinh')
        return newUpdate
      } catch (error) {
        alert.error(error)
      }
    }
  },
  mutations: {
    setAttendances(state, attendances) {
      state.attendances = attendances.reduce(
        (accumulator, currentValue) => ({
          ...accumulator,
          [currentValue.id]: currentValue
        }),
        {}
      )
    },
    setAttendance(state, attendance) {
      state.attendances = {
        ...state.attendances,
        [attendance.id]: attendance
      }
    },
    updateAttendance(state, attendance) {
      const attendances = Object.values(state.attendances).map(a => {
        if (attendance.id === a.id) return attendance
        else return a
      })
      state.attendances = keyBy(attendances, 'id')
    },
    clearStudentAttendances(state) {
      state.studentAttendances = []
    }
  },
  getters: {
    attendances: state => {
      return Object.values(state.attendances)
    },
    busTimeline: state => {
      return state.attendanceStudentSearchParams.periodTime + '-' + state.attendanceStudentSearchParams.direction
    }
  }
}
